import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertTriangle,
  ShoppingCart,
  TrendingDown,
  TrendingUp,
  Calendar,
  RefreshCw,
  CheckCircle,
  Clock,
  Package,
  ArrowRight,
  XCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn, formatCurrency } from '@/lib/utils';
import { useInventory } from '@/hooks/useInventory';
import { useRequisitions } from '@/hooks/useRequisitions';
import { useVendors } from '@/hooks/useVendors';
import { useToast } from '@/hooks/useToast';

interface ReorderSuggestion {
  id: string;
  item_id: string;
  item_name: string;
  item_code: string;
  current_stock: number;
  reorder_point: number;
  max_stock: number;
  suggested_quantity: number;
  estimated_cost: number;
  urgency: 'critical' | 'high' | 'medium' | 'low';
  days_until_stockout: number;
  preferred_vendor_id?: string;
  preferred_vendor_name?: string;
  last_order_date?: string;
  consumption_rate: number;
}

interface AutoReorderSuggestionsProps {
  onCreateRequisition?: (items: ReorderSuggestion[]) => void;
}

export function AutoReorderSuggestions({ onCreateRequisition }: AutoReorderSuggestionsProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: lowStockData, isLoading, error, refetch } = useInventory().getLowStockItems();
  const { data: vendorsData } = useVendors();
  const vendors = vendorsData?.data || [];

  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [generating, setGenerating] = useState(false);

  // Generate mock suggestions based on low stock items
  const suggestions: ReorderSuggestion[] = (lowStockData || []).map((item, index) => {
    const currentStock = item.available_quantity || 0;
    const reorderPoint = item.min_stock_level || 10;
    const maxStock = item.max_stock_level || 50;
    const suggestedQty = Math.max(reorderPoint * 2 - currentStock, reorderPoint);
    const estimatedCost = suggestedQty * (item.cost_price || 10);
    const daysUntilStockout = Math.floor(currentStock / Math.max((item.consumption_rate || 1), 1));
    
    const urgency = daysUntilStockout <= 3 ? 'critical' 
      : daysUntilStockout <= 7 ? 'high' 
      : daysUntilStockout <= 14 ? 'medium' 
      : 'low';

    const preferredVendor = vendors[index % vendors.length];

    return {
      id: `suggestion-${index}`,
      item_id: item.item_id || String(index),
      item_name: item.item_name || `Item ${index + 1}`,
      item_code: item.item_code || `ITM-${String(index + 1).padStart(4, '0')}`,
      current_stock: currentStock,
      reorder_point: reorderPoint,
      max_stock: maxStock,
      suggested_quantity: suggestedQty,
      estimated_cost: estimatedCost,
      urgency,
      days_until_stockout,
      preferred_vendor_id: preferredVendor?.vendor_id,
      preferred_vendor_name: preferredVendor?.name,
      last_order_date: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
      consumption_rate: item.consumption_rate || Math.floor(Math.random() * 5) + 1,
    };
  }).sort((a, b) => a.days_until_stockout - b.days_until_stockout);

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getUrgencyIcon = (urgency: string) => {
    switch (urgency) {
      case 'critical': return <AlertTriangle className="h-4 w-4" />;
      case 'high': return <TrendingDown className="h-4 w-4" />;
      case 'medium': return <Clock className="h-4 w-4" />;
      case 'low': return <Package className="h-4 w-4" />;
      default: return <Package className="h-4 w-4" />;
    }
  };

  const toggleItem = (id: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const selectAll = () => {
    if (selectedItems.size === suggestions.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(suggestions.map(s => s.id)));
    }
  };

  const createRequisitions = () => {
    try {
      const selectedSuggestions = suggestions.filter(s => selectedItems.has(s.id));
      if (selectedSuggestions.length === 0) {
        toast.warning('No items selected', 'Please select items to create requisitions.');
        return;
      }
      onCreateRequisition?.(selectedSuggestions);
      toast.success('Requisitions created', `Created requisitions for ${selectedSuggestions.length} items.`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create requisitions';
      toast.error('Error', message);
      console.error('Failed to create requisitions:', error);
    }
  };

  const totalCost = suggestions
    .filter(s => selectedItems.has(s.id))
    .reduce((sum, s) => sum + s.estimated_cost, 0);

  const stats = {
    critical: suggestions.filter(s => s.urgency === 'critical').length,
    high: suggestions.filter(s => s.urgency === 'high').length,
    totalCost: suggestions.reduce((sum, s) => sum + s.estimated_cost, 0),
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div
          className="p-4 rounded-lg bg-red-50 border-2 border-red-200 flex items-start gap-3"
          role="alert"
          aria-live="assertive"
        >
          <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-900">Error loading reorder suggestions</p>
            <p className="text-sm text-red-800 mt-1">{error.message}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">Critical</p>
                <p className="text-2xl font-bold text-red-700">{stats.critical}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600">High Priority</p>
                <p className="text-2xl font-bold text-orange-700">{stats.high}</p>
              </div>
              <TrendingDown className="h-8 w-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Total Suggestions</p>
                <p className="text-2xl font-bold text-blue-700">{suggestions.length}</p>
              </div>
              <ShoppingCart className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Est. Total Cost</p>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(stats.totalCost)}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={selectAll}>
            {selectedItems.size === suggestions.length ? 'Deselect All' : 'Select All'}
          </Button>
          {selectedItems.size > 0 && (
            <span className="text-sm text-text-muted">
              {selectedItems.size} selected ({formatCurrency(totalCost)})
            </span>
          )}
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/items')}>
            <Package className="mr-2 h-4 w-4" />
            Manage Items
          </Button>
          <Button onClick={createRequisitions} disabled={selectedItems.size === 0}>
            <ShoppingCart className="mr-2 h-4 w-4" />
            Create Requisitions ({selectedItems.size})
          </Button>
        </div>
      </div>

      {/* Suggestions List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Auto-Reorder Suggestions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {suggestions.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-3" />
              <p className="text-lg font-medium text-text-primary">All items are well stocked!</p>
              <p className="text-sm text-text-muted mt-1">
                No reorder suggestions at this time.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {suggestions.map((suggestion) => {
                const isSelected = selectedItems.has(suggestion.id);
                const stockPercentage = (suggestion.current_stock / suggestion.reorder_point) * 100;

                return (
                  <div
                    key={suggestion.id}
                    className={cn(
                      'p-4 border rounded-lg cursor-pointer transition-all hover:bg-background-subtle',
                      isSelected && 'border-primary bg-primary/5',
                      suggestion.urgency === 'critical' && 'border-l-4 border-l-red-500',
                      suggestion.urgency === 'high' && 'border-l-4 border-l-orange-500'
                    )}
                    onClick={() => toggleItem(suggestion.id)}
                  >
                    <div className="flex items-start gap-4">
                      {/* Checkbox */}
                      <div className={cn(
                        'w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-1',
                        isSelected ? 'border-primary bg-primary' : 'border-border'
                      )}>
                        {isSelected && <CheckCircle className="h-3 w-3 text-white" />}
                      </div>

                      {/* Urgency Badge */}
                      <Badge className={cn('shrink-0', getUrgencyColor(suggestion.urgency))}>
                        <span className="flex items-center gap-1">
                          {getUrgencyIcon(suggestion.urgency)}
                          {suggestion.urgency.toUpperCase()}
                        </span>
                      </Badge>

                      {/* Item Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-text-primary">{suggestion.item_name}</h4>
                            <p className="text-sm text-text-muted">{suggestion.item_code}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-text-primary">
                              {formatCurrency(suggestion.estimated_cost)}
                            </p>
                            <p className="text-xs text-text-muted">
                              {suggestion.suggested_quantity} units
                            </p>
                          </div>
                        </div>

                        {/* Stock Bar */}
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-text-muted">
                              Stock: {suggestion.current_stock} / {suggestion.reorder_point}
                            </span>
                            <span className={cn(
                              'font-medium',
                              suggestion.days_until_stockout <= 3 ? 'text-red-600' :
                              suggestion.days_until_stockout <= 7 ? 'text-orange-600' :
                              'text-text-muted'
                            )}>
                              {suggestion.days_until_stockout} days until stockout
                            </span>
                          </div>
                          <div className="h-2 bg-border rounded-full overflow-hidden">
                            <div
                              className={cn(
                                'h-full transition-all',
                                stockPercentage < 20 ? 'bg-red-500' :
                                stockPercentage < 50 ? 'bg-orange-500' :
                                'bg-yellow-500'
                              )}
                              style={{ width: `${Math.min(stockPercentage, 100)}%` }}
                            />
                          </div>
                        </div>

                        {/* Vendor & Last Order */}
                        <div className="flex items-center gap-4 mt-3 text-xs text-text-muted">
                          {suggestion.preferred_vendor_name && (
                            <span className="flex items-center gap-1">
                              <Package className="h-3 w-3" />
                              {suggestion.preferred_vendor_name}
                            </span>
                          )}
                          {suggestion.last_order_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Last order: {new Date(suggestion.last_order_date).toLocaleDateString()}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" />
                            {suggestion.consumption_rate}/day consumption
                          </span>
                        </div>
                      </div>

                      {/* Quick Action */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleItem(suggestion.id);
                        }}
                        className="shrink-0"
                      >
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
